<template>
    <div ref="root" class="relative">
        <div class="inline-block w-full rounded cursor-pointer">
            <button
                ref="trigger"
                :aria-expanded="open ? 'true' : 'false'"
                aria-haspopup="listbox"
                aria-labelledby="listbox-label"
                class="relative w-full rounded border border-bordergray-100 bg-white text-left focus:outline-none transition ease-in-out duration-150 sm:text-sm sm:leading-5"
                type="button"
                @click="openDropdown"
                @keydown.esc="closeDropdown"
            >
                <div class="flex items-center space-x-3">
                    <template v-if="open && searchable">
                        <div class="relative w-full">
                            <input
                                ref="search"
                                v-model="search"
                                :placeholder="$t('Search')"
                                aria-label="Search"
                                class="pl-3 pr-10 py-2 relative w-full rounded focus:outline-none transition ease-in-out duration-150 sm:text-sm sm:leading-5"
                                @click.prevent
                            >
                        </div>
                    </template>
                    <div v-else class="flex items-center space-x-3 w-full pl-3 pr-10 py-2">
                        <template v-if="!anySelected">
                            <span class="block truncate">{{ $t('Select an option') }}</span>
                        </template>
                        <template v-else-if="multiple">
                            <span class="block truncate">{{ $t('Selected') }} {{ Object.keys(selected).length }} {{ $t('options') }}</span>
                        </template>
                        <template v-else>
                            <template v-for="option in options">
                                <template v-if="option[optionKey] === selected">
                                    <slot :anySelected="anySelected" :option="option" name="selectedOption">
                                        <span class="block truncate">{{ option[optionLabel] }}</span>
                                    </slot>
                                </template>
                            </template>
                        </template>
                    </div>
                </div>
                <span class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg-vue class="h-[0.875rem] w-[0.875rem] text-gray-400" icon="font-awesome.regular.angle-down"></svg-vue>
                </span>
            </button>
        </div>
        <teleport to="body">
            <div
                v-show="open"
                ref="dropdown"
                :style="dropdownStyle"
                class="fixed z-1500 rounded bg-white shadow-lg"
                @keydown.esc="closeDropdown"
            >
                <ul
                    :style="{ maxHeight: dropdownMaxHeight }"
                    class="stage-dropdown-menu rounded text-base leading-6 shadow-xs overflow-auto focus:outline-none sm:text-sm sm:leading-5"
                    role="listbox"
                    tabindex="-1"
                >
                    <template v-if="!searchable">
                        <li
                            v-if="!required"
                            id="listbox-item-0"
                            class="text-gray-900 cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100"
                            role="option"
                            @click="selectOption(null)"
                        >
                            <div class="flex items-center space-x-3">
                                <div :class="!anySelected ? 'font-semibold' : 'font-normal'" class="font-normal block truncate">
                                    {{ $t('Select an option') }}
                                </div>
                            </div>
                        </li>
                    </template>
                    <template v-if="Object.keys(filteredOptions).length === 0">
                        <li
                            id="listbox-item-empty"
                            class="text-gray-900 select-none relative py-2 pl-3 pr-9"
                            role="option"
                        >
                            <slot :anySelected="anySelected" name="notFound">
                                <div class="flex items-center space-x-3">
                                    <div class="font-normal block truncate font-normal">
                                        {{ $t('No results found') }}
                                    </div>
                                </div>
                            </slot>
                        </li>
                    </template>
                    <template v-else>
                        <template v-for="(option, index) in filteredOptions">
                            <li
                                :id="'listbox-item-' + index"
                                class="text-gray-900 cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100"
                                role="option"
                                @click="selectOption(option[optionKey])"
                            >
                                <slot :anySelected="anySelected" :option="option" name="selectOption">
                                    <div class="flex items-center space-x-3">
                                        <template v-if="multiple">
                                            <div :class="Object.values(selected).indexOf(option[optionKey]) > -1 ? 'font-semibold' : 'font-normal'" class="font-normal block truncate">
                                                {{ option[optionLabel] }}
                                            </div>
                                        </template>
                                        <template v-else>
                                            <div :class="option[optionKey] === selected ? 'font-semibold' : 'font-normal'" class="font-normal block truncate">
                                                {{ option[optionLabel] }}
                                            </div>
                                        </template>
                                    </div>
                                </slot>
                            </li>
                        </template>
                    </template>
                </ul>
            </div>
        </teleport>
    </div>
</template>

<script>
export default {
    name: "input-select",
    emits: ['input', 'update:modelValue', 'change'],
    props: {
        modelValue: {
            type: [String, Number, Array, Object],
            required: false,
            default: undefined,
        },
        value: {
            type: [String, Number, Array, Object],
            required: false
        },
        options: {
            type: Array,
            required: true
        },
        optionKey: {
            type: String,
            default: 'id'
        },
        optionLabel: {
            type: String,
            default: 'value'
        },
        multiple: {
            type: Boolean,
            default: false
        },
        searchable: {
            type: Boolean,
            default: false
        },
        required: {
            type: Boolean,
            default: false
        },
    },
    computed: {
        selected: {
            get() {
                const selectedValue = this.modelValue !== undefined ? this.modelValue : this.value;
                if (this.multiple) {
                    return Array.isArray(selectedValue) ? selectedValue : [];
                }
                return selectedValue === undefined ? null : selectedValue;
            },
            set(selected) {
                this.$emit('update:modelValue', selected);
                this.$emit('input', selected);
            }
        },
        anySelected() {
            if (this.multiple) {
                return Array.isArray(this.selected) && this.selected.length > 0;
            }
            return this.selected !== null;
        },
        filteredOptions() {
            const self = this;
            return self.options.filter(option => {
                if (self.search) {
                    return option[self.optionLabel].toLowerCase().includes(self.search.toLowerCase());
                }
                return true;
            })
        }
    },
    watch: {
        selected() {
            this.$emit('change');
        },
        open(e) {
            const self = this;
            if (e === true) {
                self.startPositionTracking();
                self.$nextTick(function () {
                    self.updateDropdownPosition();
                });

                if (self.searchable) {
                    setTimeout(function () {
                        if (self.$refs.search) {
                            self.$refs.search.focus();
                        }
                    }, 100);
                }
            } else {
                self.stopPositionTracking();
            }
        }
    },
    data() {
        return {
            open: false,
            search: null,
            dropdownStyle: {
                left: '0px',
                top: '0px',
                width: '0px',
            },
            dropdownMaxHeight: '15rem',
        }
    },
    mounted() {
        document.addEventListener('pointerdown', this.handleDocumentPointerDown);
    },
    beforeUnmount() {
        document.removeEventListener('pointerdown', this.handleDocumentPointerDown);
        this.stopPositionTracking();
    },
    methods: {
        handleDocumentPointerDown(event) {
            const clickedInsideTrigger = this.$refs.root && this.$refs.root.contains(event.target);
            const clickedInsideDropdown = this.$refs.dropdown && this.$refs.dropdown.contains(event.target);

            if (!clickedInsideTrigger && !clickedInsideDropdown) {
                this.closeDropdown();
            }
        },
        startPositionTracking() {
            window.addEventListener('resize', this.updateDropdownPosition);
            window.addEventListener('scroll', this.updateDropdownPosition, true);
        },
        stopPositionTracking() {
            window.removeEventListener('resize', this.updateDropdownPosition);
            window.removeEventListener('scroll', this.updateDropdownPosition, true);
        },
        updateDropdownPosition() {
            if (!this.open || !this.$refs.trigger || !this.$refs.dropdown) {
                return;
            }

            const viewportGap = 4;
            const maximumMenuHeight = 240;
            const minimumMenuHeight = 80;
            const triggerRect = this.$refs.trigger.getBoundingClientRect();
            const viewportWidth = document.documentElement.clientWidth;
            const viewportHeight = document.documentElement.clientHeight;
            const spaceBelow = viewportHeight - triggerRect.bottom - viewportGap;
            const spaceAbove = triggerRect.top - viewportGap;
            const desiredHeight = Math.min(this.$refs.dropdown.scrollHeight || maximumMenuHeight, maximumMenuHeight);
            const openAbove = spaceBelow < desiredHeight && spaceAbove > spaceBelow;
            const availableHeight = Math.max(minimumMenuHeight, openAbove ? spaceAbove : spaceBelow);
            const renderedHeight = Math.min(desiredHeight, availableHeight, maximumMenuHeight);
            const width = Math.min(triggerRect.width, viewportWidth - (viewportGap * 2));
            const left = Math.min(
                Math.max(viewportGap, triggerRect.left),
                Math.max(viewportGap, viewportWidth - width - viewportGap)
            );
            const top = openAbove
                ? Math.max(viewportGap, triggerRect.top - renderedHeight - viewportGap)
                : triggerRect.bottom + viewportGap;

            this.dropdownStyle = {
                left: `${Math.round(left)}px`,
                top: `${Math.round(top)}px`,
                width: `${Math.round(width)}px`,
            };
            this.dropdownMaxHeight = `${Math.floor(Math.min(availableHeight, maximumMenuHeight))}px`;
        },
        openDropdown() {
            const self = this;
            if (self.open && self.searchable && (self.search === null || self.search === '')) {
                self.open = false;
                self.search = null;
            } else if (self.open && !self.searchable) {
                self.open = false;
            } else {
                self.open = true;
            }
        },
        closeDropdown() {
            this.open = false;
            this.search = null;
        },
        selectOption(option) {
            if (this.multiple) {
                const nextSelected = Array.isArray(this.selected) ? [...this.selected] : [];
                if (option === null) {
                    this.selected = [];
                } else {
                    if (nextSelected.indexOf(option) > -1) {
                        nextSelected.splice(nextSelected.indexOf(option), 1);
                    } else {
                        nextSelected.push(option);
                    }
                    this.selected = nextSelected;
                }
            } else {
                this.selected = option;
            }
            this.closeDropdown();
        }
    }
}
</script>
